// ─────────────────────────────────────────────────────────────────────────────
// Aísla el fallo de una sección del panel para que no se lleve el panel entero.
//
// Por qué existe (Fase 4). Al enganchar el bloque "Fundamentales" (P4-1) el panel pasó a montar
// 12 componentes que hasta ahora eran inalcanzables desde la UI. El primero que se abrió,
// Roles y Permisos, lanzaba en render — y sin barrera React desmonta el árbol completo: el usuario
// se quedaba con una pantalla en blanco, sin menú y sin forma de salir salvo recargar.
//
// El defecto concreto ya está corregido (era el enrutador del mock devolviendo un objeto donde
// la sección espera un arreglo), pero la fragilidad no: son 12 pantallas que consumen 101
// endpoints de un servicio que todavía no está desplegado. La barrera acota el daño a su celda.
//
// No sustituye a los estados de carga/error de cada sección: esos siguen siendo su
// responsabilidad. Esto es la red de seguridad para lo que se les escape.
//
// El reinicio NO se gestiona aquí: quien la monta le pasa un `key` con el slug de la sección, de
// modo que cambiar de sección crea una instancia nueva, ya sin error. Es el mismo mecanismo de
// React, sin estado derivado que mantener.
// ─────────────────────────────────────────────────────────────────────────────
import { Component, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';

interface Props extends WithTranslation {
  children: ReactNode;
}

class SectionErrorBoundaryBase extends Component<Props, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // `console.error` está permitido por la regla de lint del repo, y es lo único que queda del
    // fallo: sin esto, un error atrapado por la barrera se vuelve invisible.
    console.error('[admin] La sección falló al renderizar', error);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const { t } = this.props;
    return (
      <div className="tips-empty">
        <i className="fas fa-exclamation-triangle" />
        <p>{t('admin.sectionCrashed')}</p>
        <button
          className="btn btn--primary btn--sm"
          onClick={() => this.setState({ hasError: false })}
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }
}

export const SectionErrorBoundary = withTranslation()(SectionErrorBoundaryBase);
